/**
 * Boundary checks for the CRM v2 wire fields. Go's contract says `null` means
 * "unknown / not measurable", never 0; an older Go simply omits the field. Both
 * read as null here, and so does a value of the wrong type, so a partial or
 * malformed payload shows a dash instead of a false number or a crash.
 */

/** A finite number, else null. */
export function numberOrNull(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/** A count: a finite, non-negative number, else null. */
export function countOrNull(value: unknown): number | null {
  const n = numberOrNull(value);
  return n !== null && n >= 0 ? n : null;
}

/** A count where absence must not hide the rest of the payload: 0 stands in. */
export function countOrZero(value: unknown): number {
  return countOrNull(value) ?? 0;
}

/** A boolean, else null. */
export function booleanOrNull(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

/** A non-empty string, else null. */
export function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
